import { CustomFieldInputs, useCustomColumns, useCustomFields } from '../components/CustomFields.tsx';
import { BankDetailsFields, bankDetailsErrors, bankDetailsOf, bankDetailsValues, useUsBanking } from '../components/BankDetails.tsx';
import { addressLine, ownIdLines } from '../lib/taxes.ts';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { locale, t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { baseCurrency, day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, plain, Table } from './Reports.tsx';

type RunState = 'draft' | 'confirmed' | 'paid';
interface Employee {
  id: string;
  name: string;
  personal_no: string;
  position: string | null;
  start_date: string;
  end_date: string | null;
  salary: number;
  iban: string | null;
  bic?: string | null;
  routing_no?: string | null;
  account_no?: string | null;
  account_type?: 'checking' | 'savings' | null;
  note: string | null;
  left: boolean;
}
interface RunRow {
  id: string;
  period: string;
  work_days: number;
  state: RunState;
  gross: number;
  employee_contrib: number;
  employer_contrib: number;
  tax: number;
  deductions: number;
  net: number;
  people: number;
  paid_on: string | null;
  paid_by_method: 'bank' | 'cash' | null;
}
interface Line {
  id: string;
  employee: string;
  name: string;
  personal_no: string;
  position: string | null;
  iban: string | null;
  salary: number;
  days: number;
  bonus: number;
  deduction: number;
  note: string | null;
  gross: number;
  ss_base: number;
  hi_base: number;
  ss_employee: number;
  hi_employee: number;
  ss_employer: number;
  hi_employer: number;
  tax: number;
  net: number;
}
interface RunDetail {
  run: RunRow & { confirmed_at: string | null; confirmed_by_name: string | null };
  lines: Line[];
  rules: { year: number; confirmed: boolean };
  company: { legal_name: string; trade_name: string | null; tax_id: string | null; address: string | null; city: string | null };
}
interface Bracket {
  up_to: number | null;
  rate: number;
  over: number;
  fixed: number;
}
interface Rules {
  year: number;
  ss_employee: number;
  ss_employer: number;
  hi_employee: number;
  hi_employer: number;
  base_min: number;
  base_max: number;
  hi_capped: boolean;
  tax_after_contrib: boolean;
  brackets: Bracket[];
  confirmed_at: string | null;
  confirmed_by_name: string | null;
}

/** The business's payroll pack (modules/payroll/src/packs.ts): what its country calls things. */
interface PayPack {
  country: string;
  names: { ss: string; hi: string; tax: string };
  ready: boolean;
  whole: boolean;
  person_id: { label: string; example: string | null; pattern: string | null; rule: string | null };
}
let pack: PayPack | null = null;
/** Loads the pack once; every label below reads it, with general words until it is there. */
function usePayPack(): PayPack | null {
  const { data } = useAction<PayPack>('payroll.pack', {});
  if (data) pack = data;
  return pack;
}
const L: Record<'tax' | 'ssEmployee' | 'hiEmployee' | 'ssEmployer' | 'hiEmployer' | 'personNo', () => string> = {
  tax: () => pack?.names.tax ?? t('prl.c_tax'),
  ssEmployee: () => t('prl.part_employee', { name: pack?.names.ss ?? t('prl.ss') }),
  hiEmployee: () => t('prl.part_employee', { name: pack?.names.hi ?? t('prl.hi') }),
  ssEmployer: () => t('prl.part_employer', { name: pack?.names.ss ?? t('prl.ss') }),
  hiEmployer: () => t('prl.part_employer', { name: pack?.names.hi ?? t('prl.hi') }),
  personNo: () => pack?.person_id.label ?? t('prl.c_personal_no'),
};

type Tab = 'runs' | 'people' | 'rules';
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'runs', icon: 'cash' },
  { key: 'people', icon: 'users' },
  { key: 'rules', icon: 'percent' },
];
const STATE: Record<RunState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'warn', icon: 'edit' },
  confirmed: { tone: 'info', icon: 'check' },
  paid: { tone: 'ok', icon: 'checkCircle' },
};

/** "October 2026" for 2026-10. */
export const monthName = (period: string) => new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${period}-01T00:00:00Z`));
const lekText = (minor: number) => String(minor / 100);
const contrib = (l: Line) => l.ss_employee + l.hi_employee;

export function Payroll() {
  usePayPack();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'runs') as Tab;
  return (
    <Shell>
      <div className="page reports payroll">
        <div className="page-head">
          <div>
            <h1>{t('prl.title')}</h1>
            <p className="muted">{t('prl.subtitle')}</p>
          </div>
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('prl.title')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setParams(x.key === 'runs' ? {} : { tab: x.key }, { replace: true })}>
              <Icon name={x.icon} size={16} />
              {t(`prl.tab_${x.key}`)}
            </button>
          ))}
        </div>
        {tab === 'people' ? <PeopleTab /> : tab === 'rules' ? <RulesTab /> : <RunsTab />}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------- Payrolls */

function RunsTab() {
  const { can } = useSession();
  const { data, reload } = useAction<{ runs: RunRow[]; next: string }>('payroll.runs', {});
  // A link from elsewhere (an approval) opens one payroll: /payroll?open=<id>.
  const [open, setOpen] = useState<string | null>(() => new URLSearchParams(window.location.search).get('open'));
  const [making, setMaking] = useState(false);
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <div className="rep-grid">
      {can('payroll.manage') ? (
        <div className="toolbar">
          <Button variant="primary" icon="plus" onClick={() => setMaking(true)}>
            {t('prl.new_run')}
          </Button>
        </div>
      ) : null}
      {!data.runs.length ? (
        <EmptyState icon="cash" title={t('prl.no_runs')} body={t('prl.no_runs_body')} />
      ) : (
        <section className="panel rep-wide">
          <Table
            view="payroll.runs"
            head={[t('prl.c_month'), t('prl.c_people'), t('prl.c_gross'), L.tax(), t('prl.c_net'), t('prl.c_state')]}
            phoneHide={[1, 2, 3]}
            left={[0]}
            rows={data.runs.map((r) => ({
              key: r.id,
              onClick: () => setOpen(r.id),
              cells: [
                <span className="strong">{monthName(r.period)}</span>,
                <span className="tabular">{r.people}</span>,
                <span className="tabular">{money(r.gross)}</span>,
                <span className="tabular">{money(r.tax)}</span>,
                <span className="tabular strong">{money(r.net)}</span>,
                <StatusBadge tone={STATE[r.state].tone} icon={STATE[r.state].icon} label={t(`prl.st_${r.state}`)} />,
              ],
            }))}
          />
        </section>
      )}
      {making ? (
        <NewRun
          suggested={data.next}
          onClose={() => setMaking(false)}
          onDone={(id) => {
            setMaking(false);
            void reload();
            setOpen(id);
          }}
        />
      ) : null}
      {open ? <RunDialog id={open} onClose={() => setOpen(null)} onChanged={() => void reload()} /> : null}
    </div>
  );
}

function NewRun({ suggested, onClose, onDone }: { suggested: string; onClose: () => void; onDone: (id: string) => void }) {
  const [period, setPeriod] = useState(suggested);
  const { busy, run } = useSave();
  const submit = async () => {
    let id = '';
    if (await run(async () => (id = (await action<{ id: string }>('payroll.create_run', { period })).id))) onDone(id);
  };
  return (
    <Dialog
      title={t('prl.new_run')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={submit} disabled={!/^\d{4}-\d{2}$/.test(period)}>
            {t('prl.make')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('prl.month')} hint={t('prl.new_run_hint')}>
          <input className="input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function RunDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { can } = useSession();
  const { data, reload } = useAction<RunDetail>('payroll.run', { run: id });
  const { busy, run } = useSave();
  const [line, setLine] = useState<Line | null>(null);
  const [slip, setSlip] = useState<Line | null>(null);
  const [ask, setAsk] = useState<'confirm' | 'delete' | 'pay' | null>(null);
  const [printing, setPrinting] = useState(false);
  const changed = () => {
    void reload();
    onChanged();
  };
  if (!data) {
    return (
      <Dialog title={t('common.loading')} onClose={onClose} size="xl">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  const r = data.run;
  const draft = r.state === 'draft';
  const manage = can('payroll.manage');
  const printAll = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const csv = () =>
    downloadCsv(
      `${t('prl.file')}-${r.period}.csv`,
      [t('prl.c_name'), L.personNo(), t('prl.c_days'), t('prl.c_gross'), L.ssEmployee(), L.hiEmployee(), L.tax(), t('prl.c_deduction'), t('prl.c_net'), L.ssEmployer(), L.hiEmployer(), 'IBAN'],
      data.lines.map((l) => [l.name, l.personal_no, l.days, plain(l.gross), plain(l.ss_employee), plain(l.hi_employee), plain(l.tax), plain(l.deduction), plain(l.net), plain(l.ss_employer), plain(l.hi_employer), l.iban ?? '']),
    );
  return (
    <Dialog
      title={t('prl.run_title', { month: monthName(r.period) })}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="foot-actions pay-foot">
            <Button icon="download" onClick={csv}>
              {t('rep.download')}
            </Button>
            <Button icon="printer" onClick={printAll}>
              {t('prl.print_slips')}
            </Button>
            {draft && manage ? (
              <Button variant="ghost" icon="trash" onClick={() => setAsk('delete')}>
                {t('prl.delete')}
              </Button>
            ) : null}
          </span>
          {draft && manage ? (
            <Button variant="primary" icon="check" onClick={() => setAsk('confirm')} disabled={!data.rules.confirmed}>
              {t('prl.confirm')}
            </Button>
          ) : r.state === 'confirmed' && manage ? (
            <Button variant="primary" icon="bank" onClick={() => setAsk('pay')}>
              {t('prl.mark_paid')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack pay-run">
        <div className="pay-head">
          <StatusBadge tone={STATE[r.state].tone} icon={STATE[r.state].icon} label={t(`prl.st_${r.state}`)} />
          <span className="muted">
            {r.state === 'paid' && r.paid_on
              ? t('prl.paid_on', { date: day(r.paid_on), method: t(`prl.m_${r.paid_by_method ?? 'bank'}`) })
              : r.confirmed_by_name
                ? t('prl.confirmed_by', { name: r.confirmed_by_name })
                : tn('prl.work_days', r.work_days)}
          </span>
          {draft && manage ? <WorkDays run={r} onDone={changed} /> : null}
        </div>
        {draft && !data.rules.confirmed ? (
          <div className="callout callout-warn" role="status">
            <Icon name="alert" size={20} />
            <div>
              <p className="strong">{t('prl.rules_unconfirmed', { year: data.rules.year })}</p>
              <p className="muted">{t('prl.rules_unconfirmed_body')}</p>
            </div>
          </div>
        ) : null}
        <section className="rep-kpis" aria-label={t('prl.totals')}>
          <Kpi label={t('prl.k_gross')} value={money(r.gross)} hint={tn('prl.people', data.lines.length)} />
          <Kpi label={t('prl.k_contrib')} value={money(r.employee_contrib + r.employer_contrib)} hint={t('prl.k_contrib_hint', { employee: money(r.employee_contrib), employer: money(r.employer_contrib) })} />
          <Kpi label={L.tax()} value={money(r.tax)} />
          <Kpi label={t('prl.k_net')} value={money(r.net)} hint={t('prl.k_cost', { cost: money(r.gross + r.employer_contrib) })} />
        </section>
        <Table
          head={[t('prl.c_name'), t('prl.c_days'), t('prl.c_gross'), t('prl.c_contrib'), L.tax(), t('prl.c_net')]}
          phoneHide={[1, 2, 3, 4]}
          left={[0]}
          rows={data.lines.map((l) => ({
            key: l.id,
            onClick: () => (draft && manage ? setLine(l) : setSlip(l)),
            cells: [
              <span className="vat-party">
                {l.name}
                <span className="muted tabular">
                  {l.personal_no}
                  {l.note ? ` · ${l.note}` : ''}
                </span>
              </span>,
              <span className="tabular">
                {l.days}/{r.work_days}
              </span>,
              <span className="tabular">{money(l.gross)}</span>,
              <span className="tabular">{money(contrib(l))}</span>,
              <span className="tabular">{money(l.tax)}</span>,
              <span className="tabular strong">{money(l.net)}</span>,
            ],
          }))}
          foot={[t('acc.total'), '', <span className="tabular strong">{money(r.gross)}</span>, <span className="tabular strong">{money(r.employee_contrib)}</span>, <span className="tabular strong">{money(r.tax)}</span>, <span className="tabular strong">{money(r.net)}</span>]}
        />
        <p className="muted small">{draft && manage ? t('prl.edit_hint') : t('prl.slip_hint')}</p>
      </div>
      {line ? (
        <LineDialog
          line={line}
          workDays={r.work_days}
          onClose={() => setLine(null)}
          onSlip={() => (setSlip(line), setLine(null))}
          onDone={() => {
            setLine(null);
            changed();
          }}
        />
      ) : null}
      {slip ? <SlipDialog line={slip} data={data} onClose={() => setSlip(null)} /> : null}
      {ask === 'confirm' ? (
        <Confirm
          title={t('prl.confirm_title', { month: monthName(r.period) })}
          body={t('prl.confirm_body', { net: money(r.net) })}
          confirm={t('prl.confirm')}
          busy={busy}
          onClose={() => setAsk(null)}
          onConfirm={async () => {
            if (await run(() => action('payroll.confirm_run', { run: r.id }), t('prl.confirmed'))) {
              setAsk(null);
              changed();
            }
          }}
        />
      ) : null}
      {ask === 'delete' ? (
        <Confirm
          title={t('prl.delete_title')}
          body={t('prl.delete_body')}
          confirm={t('prl.delete')}
          danger
          busy={busy}
          onClose={() => setAsk(null)}
          onConfirm={async () => {
            if (await run(() => action('payroll.delete_run', { run: r.id }))) {
              onChanged();
              onClose();
            }
          }}
        />
      ) : null}
      {ask === 'pay' ? <PayDialog run={r} onClose={() => setAsk(null)} onDone={() => (setAsk(null), changed())} /> : null}
      {printing
        ? createPortal(
            <div className="print-sheet print-invoice">
              {data.lines.map((l) => (
                <Payslip key={l.id} line={l} data={data} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

function WorkDays({ run: r, onDone }: { run: RunRow; onDone: () => void }) {
  const [days, setDays] = useState(String(r.work_days));
  const { busy, run } = useSave();
  const n = Number(days);
  const ok = Number.isInteger(n) && n >= 1 && n <= 31;
  return (
    <form
      className="pay-days"
      onSubmit={async (e) => {
        e.preventDefault();
        if (ok && n !== r.work_days && (await run(() => action('payroll.save_run', { run: r.id, work_days: n })))) onDone();
      }}
    >
      <label>
        <span>{t('prl.work_days_label')}</span>
        <input className="input tabular" inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))} aria-invalid={!ok} />
      </label>
      <Button type="submit" variant="secondary" busy={busy} disabled={!ok || n === r.work_days}>
        {t('common.save')}
      </Button>
    </form>
  );
}

function LineDialog({ line, workDays, onClose, onDone, onSlip }: { line: Line; workDays: number; onClose: () => void; onDone: () => void; onSlip: () => void }) {
  const [days, setDays] = useState(String(line.days));
  const [bonus, setBonus] = useState(line.bonus ? lekText(line.bonus) : '');
  const [deduction, setDeduction] = useState(line.deduction ? lekText(line.deduction) : '');
  const [note, setNote] = useState(line.note ?? '');
  const { busy, run } = useSave();
  const d = Number(days);
  const b = bonus.trim() ? parseMoney(bonus) : 0;
  const x = deduction.trim() ? parseMoney(deduction) : 0;
  const daysOk = days !== '' && Number.isInteger(d) && d >= 0 && d <= workDays;
  const valid = daysOk && b !== null && b >= 0 && x !== null && x >= 0;
  const save = async () => {
    if (valid && (await run(() => action('payroll.save_line', { line: line.id, days: d, bonus: b, deduction: x, note: note.trim() || null }), t('prl.saved')))) onDone();
  };
  return (
    <Dialog
      title={line.name}
      onClose={onClose}
      footer={
        <>
          <span className="foot-actions">
            <Button variant="ghost" icon="eye" onClick={onSlip}>
              {t('prl.see_slip')}
            </Button>
          </span>
          <Button variant="primary" icon="check" busy={busy} disabled={!valid} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('prl.salary_is', { salary: money(line.salary) })}</p>
        <div className="dn-fields">
          <Field label={t('prl.days_worked')} hint={t('prl.days_hint', { n: workDays })} error={daysOk ? null : t('prl.days_bad', { n: workDays })}>
            <input className="input tabular" inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))} data-autofocus />
          </Field>
          <Field label={t('prl.bonus')} hint={t('prl.bonus_hint')} error={b === null || (b ?? 0) < 0 ? t('prl.amount_bad') : null}>
            <input className="input tabular" inputMode="decimal" value={bonus} placeholder="0" onChange={(e) => setBonus(e.target.value)} />
          </Field>
          <Field label={t('prl.deduction')} hint={t('prl.deduction_hint')} error={x === null || (x ?? 0) < 0 ? t('prl.amount_bad') : null}>
            <input className="input tabular" inputMode="decimal" value={deduction} placeholder="0" onChange={(e) => setDeduction(e.target.value)} />
          </Field>
          <Field label={t('prl.note')}>
            <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

function PayDialog({ run: r, onClose, onDone }: { run: RunRow; onClose: () => void; onDone: () => void }) {
  const [paidOn, setPaidOn] = useState(todayIso());
  const [method, setMethod] = useState<'bank' | 'cash'>('bank');
  const { busy, run } = useSave();
  return (
    <Dialog
      title={t('prl.mark_paid')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={async () => (await run(() => action('payroll.mark_paid', { run: r.id, paid_on: paidOn, method }), t('prl.paid'))) && onDone()}>
            {t('prl.mark_paid')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p>{t('prl.pay_body', { net: money(r.net) })}</p>
        <Field label={t('prl.paid_date')}>
          <input className="input" type="date" value={paidOn} max={todayIso()} onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <fieldset className="dn-owner">
          <legend>{t('prl.paid_from')}</legend>
          <div className="segmented" role="radiogroup">
            {(['bank', 'cash'] as const).map((m) => (
              <button key={m} type="button" role="radio" aria-checked={method === m} className={method === m ? 'active' : ''} onClick={() => setMethod(m)}>
                {t(`prl.m_${m}`)}
              </button>
            ))}
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}

function SlipDialog({ line, data, onClose }: { line: Line; data: RunDetail; onClose: () => void }) {
  const [printing, setPrinting] = useState(false);
  const print = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  return (
    <Dialog
      title={t('prl.slip_title', { name: line.name })}
      onClose={onClose}
      size="lg"
      footer={
        <Button variant="primary" icon="printer" onClick={print}>
          {t('inv.print')}
        </Button>
      }
    >
      <div className="inv-preview">
        <Payslip line={line} data={data} />
      </div>
      {printing
        ? createPortal(
            <div className="print-sheet print-invoice">
              <Payslip line={line} data={data} />
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

function Payslip({ line: l, data }: { line: Line; data: RunDetail }) {
  const c = data.company;
  const r = data.run;
  const rows: [string, number, boolean?][] = [
    [t('prl.s_salary', { days: l.days, of: r.work_days }), l.gross - l.bonus],
    ...(l.bonus ? [[t('prl.bonus'), l.bonus] as [string, number]] : []),
    [t('prl.s_gross'), l.gross, true],
    [L.ssEmployee(), -l.ss_employee],
    [L.hiEmployee(), -l.hi_employee],
    [L.tax(), -l.tax],
    ...(l.deduction ? [[l.note ? `${t('prl.deduction')} · ${l.note}` : t('prl.deduction'), -l.deduction] as [string, number]] : []),
    [t('prl.s_net'), l.net, true],
  ];
  return (
    <article className="inv-paper pay-slip" aria-label={t('prl.slip_title', { name: l.name })}>
      <header className="inv-paper-head">
        <div>
          <p className="inv-seller">{c.trade_name || c.legal_name}</p>
          {ownIdLines(c).map((line) => (
            <p key={line}>{line}</p>
          ))}
          <p>{addressLine(c)}</p>
        </div>
        <div className="inv-paper-title">
          <h2>{t('prl.slip')}</h2>
          <dl>
            <dt>{t('prl.month')}</dt>
            <dd>{monthName(r.period)}</dd>
          </dl>
        </div>
      </header>
      <section className="inv-paper-parties">
        <div>
          <p className="inv-label">{t('prl.employee')}</p>
          <p className="strong">{l.name}</p>
          <p>{t('prl.p_personal_no', { label: L.personNo(), no: l.personal_no })}</p>
          {l.position ? <p>{l.position}</p> : null}
          {l.iban ? <p>IBAN {l.iban}</p> : null}
        </div>
      </section>
      <table className="inv-paper-lines">
        <tbody>
          {rows.map(([label, amount, strong], i) => (
            <tr key={i} className={strong ? 'st-sum' : undefined}>
              <td>{label}</td>
              <td className="num">{money(amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="inv-label">{t('prl.s_employer')}</p>
      <table className="inv-paper-lines">
        <tbody>
          <tr>
            <td>{L.ssEmployer()}</td>
            <td className="num">{money(l.ss_employer)}</td>
          </tr>
          <tr>
            <td>{L.hiEmployer()}</td>
            <td className="num">{money(l.hi_employer)}</td>
          </tr>
          <tr className="st-sum">
            <td>{t('prl.s_cost')}</td>
            <td className="num">{money(l.gross + l.ss_employer + l.hi_employer)}</td>
          </tr>
        </tbody>
      </table>
      <p className="muted">{t('prl.s_bases', { ss: money(l.ss_base), hi: money(l.hi_base), ss_name: pack?.names.ss ?? t('prl.ss'), hi_name: pack?.names.hi ?? t('prl.hi') })}</p>
      <div className="dn-sign">
        <div>
          <span className="dn-sign-line" />
          <span>{t('prl.sign_employer')}</span>
        </div>
        <div>
          <span className="dn-sign-line" />
          <span>{t('prl.sign_employee')}</span>
        </div>
      </div>
    </article>
  );
}

/* ---------------------------------------------------------------- Employees */

function PeopleTab() {
  const { can } = useSession();
  const [all, setAll] = useState(false);
  const { data, reload } = useAction<Employee[]>('payroll.employees', { all });
  const [edit, setEdit] = useState<Employee | 'new' | null>(null);
  const extra = useCustomColumns('payroll.employee', data?.map((e) => e.id) ?? []);
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <label className="switch-row pay-left-switch">
          <span className="switch-title">{t('prl.show_left')}</span>
          <Switch checked={all} onChange={setAll} label={t('prl.show_left')} />
        </label>
        {can('payroll.manage') ? (
          <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
            {t('prl.add_employee')}
          </Button>
        ) : null}
      </div>
      {!data.length ? (
        <EmptyState icon="users" title={t('prl.no_people')} body={t('prl.no_people_body')} />
      ) : (
        <section className="panel rep-wide">
          <Table
            head={[t('prl.c_name'), t('prl.c_position'), t('prl.c_since'), ...extra.columns.map((f) => f.label), t('prl.c_salary')]}
            phoneHide={[1, 2, ...extra.columns.map((_, i) => 3 + i)]}
            left={[0, 1, ...extra.columns.map((_, i) => 3 + i)]}
            rows={data.map((e) => ({
              key: e.id,
              onClick: can('payroll.manage') ? () => setEdit(e) : undefined,
              cells: [
                <span className="vat-party">
                  {e.name}
                  <span className="muted tabular">{e.personal_no}</span>
                </span>,
                e.position ?? '—',
                <span className="tabular">{e.left && e.end_date ? t('prl.left_on', { date: day(e.end_date) }) : day(e.start_date)}</span>,
                ...extra.columns.map((f) => extra.cell(f, e.id)),
                <span className="tabular strong">{money(e.salary)}</span>,
              ],
            }))}
          />
        </section>
      )}
      {edit ? <EmployeeForm employee={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onDone={() => (setEdit(null), void reload())} /> : null}
    </div>
  );
}

/** Whether a person's number fits the country's format; any number where the format is not certain. */
function personNoError(no: string): string | null {
  if (!no) return t('prl.no_missing', { label: L.personNo() });
  const rule = pack?.person_id;
  if (!rule?.pattern) return null;
  try {
    return new RegExp(rule.pattern).test(no) ? null : (rule.rule ?? t('prl.no_bad', { label: rule.label }));
  } catch {
    return null;
  }
}

function EmployeeForm({ employee: e, onClose, onDone }: { employee: Employee | null; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(e?.name ?? '');
  const [no, setNo] = useState(e?.personal_no ?? '');
  const [position, setPosition] = useState(e?.position ?? '');
  const [start, setStart] = useState(e?.start_date ?? todayIso());
  const [end, setEnd] = useState(e?.end_date ?? '');
  const [salary, setSalary] = useState(e ? lekText(e.salary) : '');
  const [bank, setBank] = useState(bankDetailsOf(e));
  const us = useUsBanking();
  const [note, setNote] = useState(e?.note ?? '');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const cf = useCustomFields('payroll.employee', e?.id);
  const cleanNo = no.toUpperCase().replace(/[\s.]/g, '');
  const pay = parseMoney(salary);
  const errors = {
    name: name.trim().length < 2 ? t('prl.name_bad') : null,
    no: personNoError(cleanNo),
    salary: pay === null || pay < 0 ? t('prl.amount_bad') : null,
    end: end && end < start ? t('prl.end_bad') : null,
    bank: Object.values(bankDetailsErrors(bank))[0] ?? null,
  };
  const valid = !Object.values(errors).some(Boolean);
  const save = async () => {
    setTried(true);
    if (!valid || !cf.valid) return;
    const ok = await run(async () => {
      const saved = await action<{ id: string }>('payroll.save_employee', {
          employee: e?.id,
          name: name.trim(),
          personal_no: cleanNo,
          position: position.trim() || null,
          start_date: start,
          end_date: end || null,
          salary: pay,
          ...bankDetailsValues(bank, us),
          note: note.trim() || null,
        });
      await cf.save(saved.id);
    }, t('prl.saved'));
    if (ok) onDone();
  };
  const show = (k: keyof typeof errors) => (tried || (k === 'no' && cleanNo.length >= (pack?.person_id.example?.replace(/[\s.]/g, '').length ?? 99)) ? errors[k] : null);
  return (
    <Dialog
      title={e ? e.name : t('prl.add_employee')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="dn-fields">
          <Field label={t('prl.c_name')} required error={show('name')}>
            <input className="input" value={name} maxLength={80} onChange={(x) => setName(x.target.value)} data-autofocus />
          </Field>
          <Field
            label={L.personNo()}
            required
            hint={pack?.person_id.example ? t('prl.no_example', { example: pack.person_id.example }) : undefined}
            error={show('no')}
            ok={!errors.no && pack?.person_id.pattern ? t('prl.no_ok') : null}
          >
            <input className="input tabular" value={no} maxLength={30} onChange={(x) => setNo(x.target.value.toUpperCase())} />
          </Field>
          <Field label={t('prl.c_position')}>
            <input className="input" value={position} maxLength={80} onChange={(x) => setPosition(x.target.value)} />
          </Field>
          <Field label={t('prl.c_salary')} required hint={t('prl.salary_hint')} error={show('salary')}>
            <input className="input tabular" inputMode="decimal" value={salary} onChange={(x) => setSalary(x.target.value)} />
          </Field>
          <Field label={t('prl.start')} required>
            <input className="input" type="date" value={start} onChange={(x) => setStart(x.target.value)} />
          </Field>
          <Field label={t('prl.end')} hint={t('prl.end_hint')} error={show('end')}>
            <input className="input" type="date" value={end} min={start} onChange={(x) => setEnd(x.target.value)} />
          </Field>
          <Field label={t('prl.note')}>
            <input className="input" value={note} maxLength={500} onChange={(x) => setNote(x.target.value)} />
          </Field>
        </div>
        <BankDetailsFields value={bank} onChange={setBank} />
        <CustomFieldInputs cf={cf} tried={tried} />
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Rules */

function RulesTab() {
  const [year, setYear] = useState(Number(todayIso().slice(0, 4)));
  const { data, reload } = useAction<Rules>('payroll.rules', { year });
  return (
    <div className="rep-grid">
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('prl.year')}>
          {[year - 1, year, year + 1].map((y) => (
            <button key={y} role="radio" aria-checked={y === year} className={y === year ? 'active' : ''} onClick={() => setYear(y)}>
              {y}
            </button>
          ))}
        </div>
      </div>
      {!data || data.year !== year ? <Skeleton h={320} r="var(--radius-lg)" /> : <RulesForm key={`${data.year}-${data.confirmed_at}`} rules={data} onDone={() => void reload()} />}
    </div>
  );
}

function RulesForm({ rules, onDone }: { rules: Rules; onDone: () => void }) {
  const { can } = useSession();
  const edit = can('payroll.rules');
  const pctText = (n: number) => String(n);
  const [v, setV] = useState({
    ss_employee: pctText(rules.ss_employee),
    ss_employer: pctText(rules.ss_employer),
    hi_employee: pctText(rules.hi_employee),
    hi_employer: pctText(rules.hi_employer),
    base_min: lekText(rules.base_min),
    base_max: lekText(rules.base_max),
  });
  const [capped, setCapped] = useState(rules.hi_capped);
  const [afterContrib, setAfterContrib] = useState(rules.tax_after_contrib);
  const [brackets, setBrackets] = useState(rules.brackets.map((b) => ({ up_to: b.up_to === null ? '' : lekText(b.up_to), rate: String(b.rate), over: lekText(b.over), fixed: lekText(b.fixed) })));
  const { busy, run } = useSave();
  const pct = (s: string) => (/^\d+([.,]\d{1,2})?$/.test(s.trim()) ? Number(s.replace(',', '.')) : null);
  const parsed = {
    ss_employee: pct(v.ss_employee),
    ss_employer: pct(v.ss_employer),
    hi_employee: pct(v.hi_employee),
    hi_employer: pct(v.hi_employer),
    base_min: parseMoney(v.base_min),
    base_max: parseMoney(v.base_max),
  };
  const parsedBrackets = brackets.map((b, i) => ({
    up_to: i === brackets.length - 1 ? null : parseMoney(b.up_to),
    rate: pct(b.rate),
    over: b.over.trim() ? parseMoney(b.over) : 0,
    fixed: b.fixed.trim() ? parseMoney(b.fixed) : 0,
  }));
  const valid = Object.values(parsed).every((x) => x !== null) && parsedBrackets.every((b, i) => (i === brackets.length - 1 || b.up_to !== null) && b.rate !== null && b.over !== null && b.fixed !== null);
  const save = async (confirm: boolean) => {
    if (!valid) return;
    const ok = await run(() => action('payroll.save_rules', { year: rules.year, ...parsed, hi_capped: capped, tax_after_contrib: afterContrib, brackets: parsedBrackets, confirm }), confirm ? t('prl.rules_confirmed') : t('prl.saved'));
    if (ok) onDone();
  };
  const cur = baseCurrency();
  const field = (k: keyof typeof v, label: string, unit: string, hint?: string) => (
    <Field label={label} hint={hint} error={parsed[k] === null ? t('prl.amount_bad') : null}>
      <span className="input-unit">
        <input className="input tabular" inputMode="decimal" value={v[k]} disabled={!edit} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
        <span aria-hidden="true">{unit}</span>
      </span>
    </Field>
  );
  return (
    <>
      <div className={`callout ${rules.confirmed_at ? 'callout-ok' : 'callout-warn'} rep-wide`} role="status">
        <Icon name={rules.confirmed_at ? 'checkCircle' : 'alert'} size={20} />
        <div>
          <p className="strong">{rules.confirmed_at ? t('prl.rules_ok', { name: rules.confirmed_by_name ?? '—', date: day(rules.confirmed_at.slice(0, 10)) }) : t('prl.rules_unconfirmed', { year: rules.year })}</p>
          <p className="muted">{t('prl.rules_unconfirmed_body')}</p>
        </div>
      </div>
      {pack && !pack.ready && !rules.confirmed_at ? (
        <div className="callout rep-wide" role="note">
          <Icon name="info" size={20} />
          <div>
            <p className="strong">{t('prl.enter_rates')}</p>
            <p className="muted">{t('prl.enter_rates_body')}</p>
          </div>
        </div>
      ) : null}
      <section className="panel" aria-labelledby="pay-contrib">
        <div className="panel-head">
          <h2 id="pay-contrib">{t('prl.contributions')}</h2>
        </div>
        <div className="dn-fields">
          {field('ss_employee', L.ssEmployee(), '%')}
          {field('ss_employer', L.ssEmployer(), '%')}
          {field('hi_employee', L.hiEmployee(), '%')}
          {field('hi_employer', L.hiEmployer(), '%')}
          {field('base_min', t('prl.base_min'), cur, t('prl.base_min_hint'))}
          {field('base_max', t('prl.base_max', { name: pack?.names.ss ?? t('prl.ss') }), cur, t('prl.base_max_hint'))}
        </div>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('prl.hi_capped', { name: pack?.names.hi ?? t('prl.hi') })}</span>
            <span className="muted">{t('prl.hi_capped_hint', { name: pack?.names.hi ?? t('prl.hi') })}</span>
          </span>
          <Switch checked={capped} onChange={setCapped} disabled={!edit} label={t('prl.hi_capped', { name: pack?.names.hi ?? t('prl.hi') })} />
        </label>
      </section>
      <section className="panel" aria-labelledby="pay-tax">
        <div className="panel-head">
          <h2 id="pay-tax">{t('prl.tax_brackets', { name: L.tax() })}</h2>
        </div>
        <p className="muted small">{t('prl.brackets_hint')}</p>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('prl.after_contrib')}</span>
            <span className="muted">{t('prl.after_contrib_hint')}</span>
          </span>
          <Switch checked={afterContrib} onChange={setAfterContrib} disabled={!edit} label={t('prl.after_contrib')} />
        </label>
        <div className="pay-brackets">
          <span className="muted small">{t('prl.b_up_to', { cur })}</span>
          <span className="muted small">{t('prl.b_rate')}</span>
          <span className="muted small">{t('prl.b_over', { cur })}</span>
          <span className="muted small">{t('prl.b_fixed', { cur })}</span>
          {brackets.map((b, i) => {
            const last = i === brackets.length - 1;
            const set = (k: keyof typeof b, val: string) => setBrackets(brackets.map((x, j) => (j === i ? { ...x, [k]: val } : x)));
            return [
              last ? (
                <span key={`u${i}`} className="pay-b-last">
                  {t('prl.b_above')}
                </span>
              ) : (
                <input key={`u${i}`} className="input tabular" aria-label={t('prl.b_up_to', { cur })} inputMode="decimal" value={b.up_to} disabled={!edit} onChange={(e) => set('up_to', e.target.value)} />
              ),
              <input key={`r${i}`} className="input tabular" aria-label={t('prl.b_rate')} inputMode="decimal" value={b.rate} disabled={!edit} onChange={(e) => set('rate', e.target.value)} />,
              <input key={`o${i}`} className="input tabular" aria-label={t('prl.b_over', { cur })} inputMode="decimal" value={b.over} disabled={!edit} onChange={(e) => set('over', e.target.value)} />,
              <input key={`f${i}`} className="input tabular" aria-label={t('prl.b_fixed', { cur })} inputMode="decimal" value={b.fixed} disabled={!edit} onChange={(e) => set('fixed', e.target.value)} />,
            ];
          })}
        </div>
        {edit ? (
          <div className="pay-b-tools">
            <Button variant="ghost" icon="plus" disabled={brackets.length >= 10} onClick={() => setBrackets([...brackets.slice(0, -1), { up_to: '', rate: '0', over: '0', fixed: '0' }, brackets.at(-1)!])}>
              {t('prl.b_add')}
            </Button>
            <Button variant="ghost" icon="minus" disabled={brackets.length <= 1} onClick={() => setBrackets([...brackets.slice(0, -2), brackets.at(-1)!])}>
              {t('prl.b_remove')}
            </Button>
          </div>
        ) : null}
      </section>
      {edit ? (
        <div className="toolbar rep-wide pay-rule-actions">
          <Button variant="secondary" busy={busy} disabled={!valid} onClick={() => save(false)}>
            {t('prl.save_draft_rules')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!valid} onClick={() => save(true)}>
            {t('prl.confirm_rules')}
          </Button>
        </div>
      ) : (
        <p className="muted small rep-wide">{t('prl.rules_who')}</p>
      )}
    </>
  );
}
